<template>
  <v-container fluid class="pa-">
    <v-row>
      <v-col cols="12">
        <v-card elevation="2">
          <!-- Header -->
          <v-card-title class="primary white--text">
            <v-icon left color="white" large>mdi-package-variant</v-icon>
            <span class="text-h5">{{ $t('item.button.item') }}</span>
          </v-card-title>

          <v-divider></v-divider>

          <!-- Filters Section -->
          <v-card-text class="pt-">
            <v-expand-transition>
              <div>
                <v-row>
                  <!-- Category Filter -->
                  <v-col cols="12" sm="6" md="3">
                    <v-autocomplete
                      v-model="filters.category_id"
                      :items="categories"
                      item-text="name"
                      item-value="id"
                      :label="$t('item.label.category')"
                      outlined
                      dense
                      clearable
                      hide-details
                      prepend-inner-icon="mdi-shape"
                    >
                      <template v-slot:item="{ item }">
                        <v-list-item-content>
                          <v-list-item-title>{{ item.name }}</v-list-item-title>
                        </v-list-item-content>
                      </template>
                    </v-autocomplete>
                  </v-col>

                  <!-- Supplier Filter -->
                  <v-col cols="12" sm="6" md="3">
                    <v-autocomplete
                      v-model="filters.supplier_id"
                      :items="suppliers"
                      item-text="name"
                      item-value="id"
                      :label="$t('item.label.supplier')"
                      outlined
                      dense
                      clearable
                      hide-details
                      prepend-inner-icon="mdi-account-network"
                    >
                      <template v-slot:item="{ item }">
                        <v-list-item-content>
                          <v-list-item-title>{{ item.name }}</v-list-item-title>
                        </v-list-item-content>
                      </template>
                    </v-autocomplete>
                  </v-col>

                  <!-- Item Type Filter -->
                  <v-col cols="12" sm="6" md="3">
                    <v-select
                      v-model="filters.item_type"
                      :items="itemTypes"
                      :label="$t('item.label.item_type')"
                      outlined
                      dense
                      clearable
                      hide-details
                      prepend-inner-icon="mdi-tag"
                    ></v-select>
                  </v-col>

                  <!-- Search Filter -->
                  <v-col cols="12" sm="6" md="3">
                    <v-text-field
                      v-model="filters.search"
                      :label="$t('button.search')"
                      placeholder="Search by name or code"
                      outlined
                      dense
                      clearable
                      hide-details
                      prepend-inner-icon="mdi-magnify"
                      @keyup.enter="applyFilters"
                    ></v-text-field>
                  </v-col>
                </v-row>

                <!-- Filter Actions -->
                <v-row class="mt-2">
                  <v-col cols="12" class="text-right">
                    <v-btn
                      color="primary"
                      @click="applyFilters"
                      :loading="loading"
                      class="mr-2"
                    >
                      <v-icon left>mdi-filter</v-icon>
                      {{ $t('item.button.filter') }}
                    </v-btn>
                    <v-btn
                      color="grey"
                      outlined
                      @click="clearFilters"
                      :disabled="loading"
                    >
                      <v-icon left>mdi-filter-remove</v-icon>
                      {{ $t('item.button.clear_filter') }}
                    </v-btn>
                  </v-col>
                </v-row>
              </div>
            </v-expand-transition>
          </v-card-text>

          <v-divider></v-divider>

          <!-- Data Table -->
          <v-card-text>
            <v-data-table
              :headers="headers"
              :items="items"
              :loading="loading"
              :options.sync="options"
              :server-items-length="pagination.total"
              :footer-props="{
                'items-per-page-options': [10, 15, 30, 40, 100],
                'show-first-last-page': true,
                'show-current-page': true
              }"
              class="elevation-1"
              @update:options="handleTableUpdate"
            >
              <!-- Loading -->
              <template v-slot:loading>
                <v-skeleton-loader
                  type="table-row@10"
                ></v-skeleton-loader>
              </template>

              <!-- Photo Column -->
              <template v-slot:[`item.photo`]="{ item }">
                <v-avatar
                  v-if="item.visual_type === 'colour'"
                  size="40"
                  :color="item.hex_colour ? `#${item.hex_colour}` : '#cccccc'"
                  tile
                  rounded
                ></v-avatar>
                <v-avatar
                  v-else
                  size="40"
                  tile
                  rounded
                >
                  <v-img :src="item.image" :alt="item.name"></v-img>
                </v-avatar>
              </template>

              <!-- Code Column -->
              <template v-slot:[`item.code`]="{ item }">
                <v-chip small label outlined color="primary">
                  <strong>{{ item.code }}</strong>
                </v-chip>
              </template>

              <!-- Name Column -->
              <template v-slot:[`item.name`]="{ item }">
                <div class="text-subtitle-2">{{ item.name }}</div>
                <div v-if="item.description" class="text-caption grey--text">
                  {{ item.description.substring(0, 50) }}{{ item.description.length > 50 ? '...' : '' }}
                </div>
              </template>

              <!-- Category Column -->
              <template v-slot:[`item.category`]="{ item }">
                <v-chip
                  v-if="item.category"
                  small
                  color="info"
                  text-color="white"
                >
                  {{ item.category.name }}
                </v-chip>
              </template>

              <!-- Unit Price Column -->
              <template v-slot:[`item.unit_price`]="{ item }">
                <div class="text-subtitle-2 font-weight-bold success--text">
                  {{ item.unit_price | currency('XAF ') }}
                </div>
              </template>

              <!-- Purchase Price Column -->
              <template v-slot:[`item.purchase_price`]="{ item }">
                <div class="text-body-2">
                  {{ item.purchase_price | currency('XAF ') }}
                </div>
              </template>

              <!-- Item Type Column -->
              <template v-slot:[`item.item_type`]="{ item }">
                <v-chip
                  small
                  :color="item.item_type === 'product' ? 'success' : 'info'"
                  text-color="white"
                >
                  {{ item.item_type.toUpperCase() }}
                </v-chip>
              </template>

              <!-- Actions Column -->
              <template v-slot:[`item.actions`]="{ item }">
                <v-menu offset-y>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      color="primary"
                      small
                      v-bind="attrs"
                      v-on="on"
                    >
                      <v-icon left small>mdi-pencil</v-icon>
                      {{ $t('button.edit') }}
                      <v-icon right small>mdi-menu-down</v-icon>
                    </v-btn>
                  </template>
                  <v-list dense>
                    <v-list-item @click="showEditDialog(item.id)">
                      <v-list-item-icon>
                        <v-icon color="primary">mdi-pencil</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title>{{ $t('button.edit') }}</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item @click="beforeArchive(item.id)">
                      <v-list-item-icon>
                        <v-icon color="warning">mdi-archive</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title>{{ $t('button.archive') }}</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item @click="$router.push({name: 'stock-item', params: {item_id: item.id}})">
                      <v-list-item-icon>
                        <v-icon color="info">mdi-information</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title>{{ $t('item.button.stock') }}</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </template>

              <!-- No Data -->
              <template v-slot:no-data>
                <div class="text-center pa-8">
                  <v-icon size="64" color="grey lighten-1">mdi-package-variant-closed</v-icon>
                  <div class="text-h6 grey--text mt-4">{{ $t('no_data') }}</div>
                </div>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Create/Edit Dialog -->
    <create-edit-item-vuetify
      :display="displayEditDialog"
      @close="closeEditDialog"
      @created="newItem"
      @updated="itemUpdated"
      :item="item"
    ></create-edit-item-vuetify>
  </v-container>
</template>

<script>
import CreateEditItemVuetify from './Modal/CreateEditItemVuetify'
import ErrorHandlingMixin from '@/mixins/ErrorHandlingMixin.js'

export default {
  name: 'IndexVuetify',
  mixins: [ErrorHandlingMixin],
  components: { CreateEditItemVuetify },
  data () {
    return {
      items: [],
      item: {},
      displayEditDialog: false,
      loading: false,
      filters: {
        category_id: null,
        supplier_id: null,
        item_type: null,
        search: ''
      },
      options: {
        page: 1,
        itemsPerPage: 10
      },
      pagination: {
        current_page: 1,
        per_page: 10,
        total: 0,
        last_page: 1
      },
      categories: [],
      suppliers: [],
      itemTypes: [
        { text: this.$t('item.label.type.product'), value: 'product' },
        { text: this.$t('item.label.type.service'), value: 'service' }
      ],
      headers: [
        { text: 'Photo', value: 'photo', sortable: false, align: 'center', width: '80px' },
        { text: this.$t('label.ref'), value: 'code', sortable: true },
        { text: this.$t('label.name'), value: 'name', sortable: true },
        { text: this.$t('item.label.family'), value: 'category', sortable: false },
        { text: this.$t('item.label.price'), value: 'unit_price', sortable: true, align: 'right' },
        { text: this.$t('item.label.purchase_price'), value: 'purchase_price', sortable: true, align: 'right' },
        { text: this.$t('item.label.type.type'), value: 'item_type', sortable: true },
        { text: this.$t('label.action'), value: 'actions', sortable: false, align: 'center', width: '150px' }
      ]
    }
  },
  methods: {
    showEditDialog (id) {
      this.item = this.items.find(d => d.id === id)
      this.displayEditDialog = true
    },
    closeEditDialog () {
      this.item = {}
      this.displayEditDialog = false
    },
    newItem (data) {
      this.items.unshift(data)
      this.pagination.total++
    },
    itemUpdated (data) {
      const index = this.items.findIndex(d => d.id === data.id)
      if (index > -1) {
        this.$set(this.items, index, data)
      }
    },
    beforeArchive (id) {
      this.askQuestion(this.$t('item.confirm.archive'), async () => {
        try {
          await this.withLoading(async () => {
            await this.$itemService.archiveItem(id)
            this.showSuccess(this.$t('item.message.archived'))
            const index = this.items.findIndex(d => d.id === id)
            if (index > -1) {
              this.items.splice(index, 1)
              this.pagination.total--
            }
          }, 'loading')
        } catch (error) {
          console.log('Component caught error in archiveItem:', error)
        }
      })
    },
    async fetchItems () {
      this.loading = true
      try {
        const params = {
          ...this.filters,
          page: this.options.page,
          per_page: this.options.itemsPerPage
        }
        const res = await this.$itemService.getPaginatedItems(params)
        this.items = res.data
        this.pagination.current_page = Number(res.meta.current_page)
        this.pagination.per_page = Number(res.meta.per_page)
        this.pagination.total = Number(res.meta.total)
        this.pagination.last_page = Number(res.meta.last_page)
      } catch (error) {
        console.log('Component caught error in getItems:', error)
      } finally {
        this.loading = false
      }
    },
    handleTableUpdate (options) {
      this.options = options
      this.fetchItems()
    },
    applyFilters () {
      this.options.page = 1
      this.fetchItems()
    },
    clearFilters () {
      this.filters = {
        category_id: null,
        supplier_id: null,
        item_type: null,
        search: ''
      }
      this.options.page = 1
      this.fetchItems()
    },
    async fetchCategories () {
      this.categories = await this.$categoryService.getCategories()
    },
    async fetchSuppliers () {
      this.suppliers = await this.$supplierService.getSuppliers()
    }
  },
  async mounted () {
    this.$emit('parent-title', this.$t('item.title.item'))
    await Promise.all([
      this.fetchCategories(),
      this.fetchSuppliers()
    ])
    await this.fetchItems()
  },
  created () {
    this.$parent.$on('new-item', this.newItem)
  }
}
</script>

<style scoped>
::v-deep .v-data-table > .v-data-table__wrapper > table > tbody > tr > td {
  padding: 12px 16px;
}

::v-deep .v-data-table > .v-data-table__wrapper > table > thead > tr > th {
  font-weight: 600;
  font-size: 0.875rem;
}
</style>
